import { useState, useEffect, useCallback } from 'react';

import { Dayjs } from 'dayjs';
import { IBread } from '@components/Breadcrumb/BreadCrumb';
import { CONST_OPERATOR_INVOICE } from 'constants/language';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  apiAgencyListAllInvoiceDetail,
  apiOperatorTransferAgency,
  apiOperatorVerifyOTP,
  apiUpdateStatusAgencyInvoice,
} from 'api/operator';
import { responseCode, responseSuccess } from 'helper/responseSuccess/responseSuccess';
import { exportPDFsToZipForAgency } from 'helper/export';
import { EKeyCode, EStatusInvoiceAgency } from 'constants/constants';
import { IAgencyBasicOperatorInvoice, IDownTime, IFilterPage, defaultPageInvoiceDetailAgency } from 'constants/invoice';
import { message } from 'antd';
import { getDayjsByTimeZone, isRegexMonth } from 'helper/date';
import { useHookAgencyListInvoice } from '@hooks/useOperator';
import { _isNumber } from 'helper';
import { useQueryClient } from 'react-query';
import { AGENCY_INFO_INVOICE_ID, AGENCY_INVOICE_DETAIL_ID } from 'constants/keyQuery';

const useListInvoiceAgency = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search);
  const paramName = query.get('key_word') ?? '';
  const paramDate = query.get('date') ?? '';
  const paramPage = _isNumber(query.get('page'), 1);

  const [paramSearch, setParamsearch] = useState<any>({
    key_word: '',
    date: getDayjsByTimeZone().format('YYYY-MM'),
    page: 1,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
  const [listInvoice, setListInvoice] = useState<IAgencyBasicOperatorInvoice[]>();
  const [listInvoiceAllDetail, setListInvoiceAllDetail] = useState<IAgencyBasicOperatorInvoice[]>();
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(getDayjsByTimeZone(paramSearch.date));
  const [currentMonthInput, setCurrentMonthInput] = useState<Dayjs | undefined>(getDayjsByTimeZone(paramSearch.date));
  const [keyWord, setKeyWord] = useState<string>();
  const [page, setPage] = useState<IFilterPage>({ ...defaultPageInvoiceDetailAgency, per_page: 10 });

  const [otp, setOtp] = useState('');
  const [isOpenOTP, setIsOpenOTP] = useState<boolean>(false);
  const [isLoadingSubmitOTP, setLoadingSubmitOTP] = useState<boolean>(false);
  const [isLoadingTransfer, setIsLoadingTransfer] = useState<boolean>(false);
  const [dateDownTime, setDateDownTime] = useState<IDownTime>();
  const [invoiceInfo, setInvoiceInfo] = useState<IAgencyBasicOperatorInvoice>();

  const [totalPayment, setTotalPayment] = useState<number>(0);

  const { data: dataListInvoice, isLoading: _isLoading } = useHookAgencyListInvoice({
    ...paramSearch,
  });

  const queryClient = useQueryClient();
  queryClient.removeQueries(AGENCY_INFO_INVOICE_ID);
  queryClient.removeQueries(AGENCY_INVOICE_DETAIL_ID);

  useEffect(() => {
    setIsLoading(_isLoading);
  }, [_isLoading]);

  useEffect(() => {
    if (dataListInvoice) {
      setListInvoice(dataListInvoice?.data);
      setPage((prevPage: any) => ({
        ...prevPage,
        total: dataListInvoice?.total,
        page: dataListInvoice?.page,
      }));

      setParamsearch((prevPage: any) => ({
        ...prevPage,
        page: dataListInvoice?.page,
      }));
    }
  }, [dataListInvoice]);

  const BREADS: IBread[] = [
    {
      name: CONST_OPERATOR_INVOICE.LIST_AGENCY_INVOICE,
      path: '',
    },
  ];

  useEffect(() => {
    setParamsearch((prevPage: any) => ({
      ...prevPage,
      key_word: paramName,
    }));
    setKeyWord(paramName);
  }, [paramName]);

  useEffect(() => {
    setParamsearch((prevPage: any) => ({
      ...prevPage,
      page: paramPage,
    }));
    setPage((prevPage: any) => ({
      ...prevPage,
      page: paramPage,
    }));
  }, [paramPage]);

  useEffect(() => {
    let _date = getDayjsByTimeZone();
    if (paramDate && isRegexMonth(paramDate)) {
      _date = getDayjsByTimeZone(paramDate);
    }
    setCurrentMonth(_date);
    setCurrentMonthInput(_date);
    setParamsearch((prevPage: any) => ({
      ...prevPage,
      date: _date.format('YYYY-MM'),
    }));
  }, [paramDate]);

  const exportAllInvoicePDF = useCallback(async () => {
    if (isLoadingExport) {
      return;
    }
    setIsLoadingExport(true);
    try {
      const response = await apiAgencyListAllInvoiceDetail({
        date: currentMonth?.format('YYYY-MM'),
        key_word: keyWord,
      });
      if (responseSuccess(response)) {
        setListInvoiceAllDetail(response.data);
        setTimeout(() => {
          exportPDFsToZipForAgency(response.data);
          setIsLoadingExport(false);
        }, 100);
      }
    } catch (error) {
      //
    }
  }, [isLoadingExport, currentMonth, keyWord]);

  const changeStatusInvoice = useCallback(
    async (id: number) => {
      setIsLoading(true);
      try {
        const response = await apiUpdateStatusAgencyInvoice(id, EStatusInvoiceAgency.TRANSFER_COMPLETED);
        if (responseSuccess(response)) {
          const dataTemp = [...(listInvoice || [])];
          dataTemp.forEach((ele: IAgencyBasicOperatorInvoice) => {
            if (ele.id === id) {
              ele.status = EStatusInvoiceAgency.TRANSFER_COMPLETED;
            }
          });
          setListInvoice(dataTemp);
        }
      } catch (error) {
        if (responseCode(error) === 'K001') {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgCantConfirmInvoice);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [listInvoice]
  );

  const getListInvoiceDetailAll = async (date?: Dayjs, keyWordCall?: string) => {
    try {
      const response = await apiAgencyListAllInvoiceDetail({
        date: date?.format('YYYY-MM'),
        key_word: keyWordCall,
      });
      if (responseSuccess(response)) {
        setListInvoiceAllDetail(response.data);
      }
    } catch (error) {
      //
    }
  };

  const changePaging = useCallback(
    (pageChange: number) => {
      setPage({
        ...page,
        page: pageChange,
      });
      setParamsearch((prevPage: any) => ({
        ...prevPage,
        page: pageChange,
      }));
      navigate({
        pathname,
        search: `?${createSearchParams({ ...paramSearch, page: pageChange })}`,
      });
    },
    // eslint-disable-next-line
    [page]
  );

  const refeshNumberPage = useCallback(() => {
    setParamsearch((prevPage: any) => ({
      ...prevPage,
      key_word: keyWord,
      page: 1,
    }));
    navigate({
      pathname,
      search: `?${createSearchParams({ ...paramSearch, key_word: keyWord, page: 1 })}`,
    });
    // eslint-disable-next-line
  }, [keyWord]);

  useEffect(() => {
    // getListInvoice(page.page, currentMonth, keyWord);
    getListInvoiceDetailAll(currentMonth, keyWord);
    // eslint-disable-next-line
  }, [currentMonth, keyWord, page.page]);

  const onKeyDownKeyWord = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === EKeyCode.ENTER) {
        e.preventDefault();
        refeshNumberPage();
      }
    },
    [refeshNumberPage]
  );

  const onChangeMonth = (e: Dayjs | any) => {
    if (e) {
      setCurrentMonth(e);
      setCurrentMonthInput(e);
      setParamsearch((prevPage: any) => ({
        ...prevPage,
        date: e?.format('YYYY-MM'),
        page: 1,
      }));
      navigate({
        pathname,
        search: `?${createSearchParams({ ...paramSearch, date: e?.format('YYYY-MM'), page: 1 })}`,
      });
    } else {
      setCurrentMonthInput(undefined);
    }
  };

  const onChangeKeyWord = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyWord(e.target.value);
  };

  useEffect(() => {
    if (listInvoiceAllDetail) {
      const sum = listInvoiceAllDetail.reduce((total, item: IAgencyBasicOperatorInvoice) => {
        return total + item.total_payment;
      }, 0);
      setTotalPayment(sum);
    }
  }, [listInvoiceAllDetail]);

  const onClickTransferAgency = useCallback(
    async (invoiceInfo: any) => {
      if (invoiceInfo) {
        setInvoiceInfo(invoiceInfo);
        try {
          setIsLoadingTransfer(true);
          const response = await apiOperatorTransferAgency(
            Number(invoiceInfo.id),
            EStatusInvoiceAgency.WAITING_OPERATOR_VERIFY
          );
          setInvoiceInfo({
            ...invoiceInfo,
            status: EStatusInvoiceAgency.WAITING_OPERATOR_VERIFY,
          });
          if (responseSuccess(response)) {
            if (response?.data && response?.data.status === EStatusInvoiceAgency.WAITING_OPERATOR_VERIFY) {
              setDateDownTime(response?.data);
              setIsOpenOTP(true);
              setOtp('');

              if (listInvoice?.length) {
                let _listInvoice: IAgencyBasicOperatorInvoice[] = [];
                listInvoice.forEach((element) => {
                  if (element.id === invoiceInfo.id) {
                    element.status = EStatusInvoiceAgency.WAITING_OPERATOR_VERIFY;
                    element.date_timeout = response?.data.date_timeout;
                  }
                  _listInvoice.push(element);
                });
                setListInvoice(_listInvoice);
              }
            }
          }
        } catch (error: any) {
          if (responseCode(error) === 'K002') {
            message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgOperatorNotPhone);
          }
          if (responseCode(error) === 'K003') {
            message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgAgencyNotSettingBank);
          }
          if (responseCode(error) === 'K004') {
            message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgAgencyTransferInprogress);
          }
        } finally {
          setIsLoadingTransfer(false);
        }
      }
    },
    [listInvoice]
  );

  const onSubmitOTP = async (otp: any) => {
    try {
      if (invoiceInfo) {
        if (!otp.length) {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPEmpty);
        } else if (otp.length === 6) {
          setLoadingSubmitOTP(true);
          const response = await apiOperatorVerifyOTP(Number(invoiceInfo.id), { otp: otp });
          if (responseSuccess(response)) {
            setIsOpenOTP(false);
            setOtp('');
            setInvoiceInfo({
              ...invoiceInfo,
              status: EStatusInvoiceAgency.WAITING_OPERATOR_VERIFIED,
            });
            if (listInvoice?.length) {
              let _listInvoice: IAgencyBasicOperatorInvoice[] = [];
              listInvoice.forEach((element) => {
                if (element.id === invoiceInfo.id) {
                  element.status = EStatusInvoiceAgency.WAITING_OPERATOR_VERIFIED;
                }
                _listInvoice.push(element);
              });
              setListInvoice(_listInvoice);
            }
            message.success(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPSccuess);
          } else {
            if (responseCode(response) === 'G001') {
              message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPFalse);
            }
          }
        } else {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPNotFull);
        }
      }
    } catch (error) {
    } finally {
      setLoadingSubmitOTP(false);
    }
  };

  return {
    navigate,
    BREADS,
    listInvoice,
    currentMonth,
    currentMonthInput,
    onChangeMonth,
    page,
    changePaging,
    isLoading,
    listInvoiceAllDetail,
    keyWord,
    onChangeKeyWord,
    onKeyDownKeyWord,
    refeshNumberPage,
    exportAllInvoicePDF,
    isLoadingExport,
    changeStatusInvoice,
    totalPayment,
    isOpenOTP,
    setIsOpenOTP,
    otp,
    setOtp,
    onSubmitOTP,
    isLoadingSubmitOTP,
    dateDownTime,
    onClickTransferAgency,
    invoiceInfo,
    isLoadingTransfer,
  };
};

export default useListInvoiceAgency;
